import { Injectable, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { PermissionService } from './permission.service';
import {
  AuthenticationResult,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
  AccountInfo
} from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private userSubject = new BehaviorSubject<AccountInfo | null>(null);
  user$ = this.userSubject.asObservable();

  private authReadySubject = new BehaviorSubject<boolean>(false);
  authReady$ = this.authReadySubject.asObservable();

  private msalInitialized = new BehaviorSubject<boolean>(false);
  public msalInitialized$ = this.msalInitialized.asObservable();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private permissionService: PermissionService
  ) {
    this.initializeMsal();
  }

  private initializeMsal(): void {
    this.msalService.initialize().subscribe({
      next: () => {
        console.log('MSAL initialized successfully');
        this.msalInitialized.next(true);
        this.setupMsalHandlers();
      },
      error: (error) => {
        console.error('MSAL initialization failed:', error);
      }
    });
  }

  private setupMsalHandlers(): void {
    this.msalService.handleRedirectObservable().subscribe((result) => {
      if (result && result.account) {
        this.msalService.instance.setActiveAccount(result.account);
      } else {
        const existingAccounts = this.msalService.instance.getAllAccounts();
        if (existingAccounts.length > 0) {
          this.msalService.instance.setActiveAccount(existingAccounts[0]);
        }
        else {
          // No active session — redirect to login
          this.loginRedirect();
        }
      }
      // Only after handling redirect, update the display
      this.setLoginDisplay();
      this.authReadySubject.next(true);
    });

    this.msalService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });
  }

  private setLoginDisplay() {
    const accounts = this.msalService.instance.getAllAccounts();
    const account = accounts.length > 0 ? accounts[0] : null;

    if (account) {
      this.msalService.instance.setActiveAccount(account);
      // Fetch user permissions after successful login
      this.permissionService.fetchUserPermissions().subscribe({
        next: (permissions) => {
          console.log('User permissions loaded');
        },
        error: (error) => {
          console.error('Failed to load user permissions:', error);
        }
      });
    }

    this.userSubject.next(account);
  }

  private checkAndSetActiveAccount() {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
        this.userSubject.next(accounts[0]);
      }
    }
  }

  loginRedirect() {
    this.msalService.loginRedirect();
  }

  logoutRedirect() {
    // Clear permissions on logout
    this.permissionService.clearPermissions();
    this.msalService.logoutRedirect();
  }

  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  isMsalInitialized(): boolean {
    return this.msalInitialized.value;
  }

  getMsalInitializedObservable(): Observable<boolean> {
    return this.msalInitialized$;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}


